import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Header from "../../components/header";
import AIChatSidebar from "../../components/ai/AIChatSidebar";
import AIChatWindow from "../../components/ai/AIChatWindow";
import {
  createConversation,
  getConversations,
  getConversation,
  deleteConversation,
  sendMessage,
} from "../../functions/ai";
import "./JornalistaAI.css";

export default function JornalistaAI() {
  const { user } = useSelector((state) => ({ ...state }));

  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedTitles, setSelectedTitles] = useState([]);
  const [paragraphCount, setParagraphCount] = useState(5);
  const [showParagraphModal, setShowParagraphModal] = useState(false);
  const [pendingTitleIndex, setPendingTitleIndex] = useState(null);

  // Load conversations
  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    try {
      const data = await getConversations("jornalista", user.token);
      setConversations(data);
    } catch (error) {
      console.error("Error loading conversations:", error);
    }
  };

  const handleNewConversation = async () => {
    try {
      const conversation = await createConversation(
        "jornalista",
        "Nova noticia",
        null,
        user.token
      );
      setConversations([conversation, ...conversations]);
      setActiveConversation(conversation);
      setMessages([]);
      setSelectedTitles([]);
    } catch (error) {
      console.error("Error creating conversation:", error);
    }
  };

  const handleSelectConversation = async (conversation) => {
    try {
      setActiveConversation(conversation);
      const data = await getConversation(conversation._id, user.token);
      setMessages(data.messages);

      // Extract any title suggestions from messages
      const lastAssistantMsg = [...data.messages]
        .reverse()
        .find((m) => m.role === "assistant" && m.titleSuggestions?.length > 0);

      if (lastAssistantMsg) {
        setSelectedTitles(lastAssistantMsg.titleSuggestions);
      } else {
        setSelectedTitles([]);
      }
    } catch (error) {
      console.error("Error loading conversation:", error);
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    try {
      await deleteConversation(conversationId, user.token);
      setConversations(conversations.filter((c) => c._id !== conversationId));
      if (activeConversation?._id === conversationId) {
        setActiveConversation(null);
        setMessages([]);
        setSelectedTitles([]);
      }
    } catch (error) {
      console.error("Error deleting conversation:", error);
    }
  };

  const handleSendMessage = async (content) => {
    let currentConversation = activeConversation;

    // Create conversation if none exists
    if (!currentConversation) {
      try {
        currentConversation = await createConversation(
          "jornalista",
          content.substring(0, 50),
          null,
          user.token
        );
        setConversations([currentConversation, ...conversations]);
        setActiveConversation(currentConversation);
      } catch (error) {
        console.error("Error creating conversation:", error);
        return;
      }
    }

    // Add user message optimistically
    const tempUserMessage = {
      _id: Date.now().toString(),
      role: "user",
      content,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMessage]);
    setIsLoading(true);

    try {
      const response = await sendMessage(
        currentConversation._id,
        content,
        false,
        user.token
      );

      // Update messages with actual response
      setMessages((prev) => [
        ...prev.filter((m) => m._id !== tempUserMessage._id),
        response.userMessage,
        response.assistantMessage,
      ]);

      // Store title suggestions if present
      if (response.assistantMessage.titleSuggestions?.length > 0) {
        setSelectedTitles(response.assistantMessage.titleSuggestions);
      }

      // Update conversation title if it was the first message
      if (messages.length === 0) {
        setConversations((prev) =>
          prev.map((c) =>
            c._id === currentConversation._id
              ? { ...c, title: content.substring(0, 50) }
              : c
          )
        );
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => prev.filter((m) => m._id !== tempUserMessage._id));
    } finally {
      setIsLoading(false);
    }
  };

  const handleTitleSelect = (titleIndex) => {
    setPendingTitleIndex(titleIndex);
    setShowParagraphModal(true);
  };

  const handleConfirmArticle = () => {
    if (pendingTitleIndex !== null) {
      const message = `Titulo ${pendingTitleIndex + 1} com ${paragraphCount} paragrafos`;
      handleSendMessage(message);
      setShowParagraphModal(false);
      setPendingTitleIndex(null);
    }
  };

  const welcomeContent = {
    title: "Sou o Jornalista AI",
    subtitle: "Ajudo a criar noticias de imprensa profissionais e bem estruturadas",
    suggestions: [
      {
        icon: "📰",
        text: "Escrever uma noticia sobre um evento",
        prompt: "Preciso escrever uma noticia sobre um evento que aconteceu na minha cidade",
      },
      {
        icon: "🏢",
        text: "Noticia sobre uma empresa",
        prompt: "Quero criar uma noticia sobre o lancamento de um novo produto de uma empresa",
      },
      {
        icon: "⚽",
        text: "Noticia desportiva",
        prompt: "Preciso escrever uma noticia sobre um jogo de futebol",
      },
      {
        icon: "🎭",
        text: "Noticia cultural",
        prompt: "Quero criar uma noticia sobre um festival de musica ou evento cultural",
      },
    ],
  };

  // Custom title suggestions rendering
  const extraActions = selectedTitles.length > 0 && (
    <div className="jornalista_actions">
      <div className="titles_panel">
        <h3>Titulos Sugeridos</h3>
        <p>Clique num titulo para gerar a noticia completa:</p>
        <div className="titles_list">
          {selectedTitles.map((title, index) => (
            <button
              key={index}
              className="title_option"
              onClick={() => handleTitleSelect(index)}
              disabled={isLoading}
            >
              <span className="title_number">{index + 1}</span>
              <span className="title_text">{title}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="jornalista_page">
      <Header page="ai" />
      <div className="jornalista_container">
        <AIChatSidebar
          conversations={conversations}
          activeConversation={activeConversation}
          onSelectConversation={handleSelectConversation}
          onNewConversation={handleNewConversation}
          onDeleteConversation={handleDeleteConversation}
          type="jornalista"
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />

        <AIChatWindow
          messages={messages}
          onSendMessage={handleSendMessage}
          isLoading={isLoading}
          type="jornalista"
          conversation={activeConversation}
          showWelcome={true}
          welcomeContent={welcomeContent}
          inputPlaceholder="Descreva a noticia que deseja criar..."
          user={user}
          extraActions={extraActions}
        />
      </div>

      {/* Paragraph Count Modal */}
      {showParagraphModal && (
        <div className="modal_overlay" onClick={() => setShowParagraphModal(false)}>
          <div className="modal_content paragraph_modal" onClick={(e) => e.stopPropagation()}>
            <h2>Configurar Artigo</h2>
            <p>
              Titulo selecionado: <strong>{selectedTitles[pendingTitleIndex]}</strong>
            </p>

            <div className="paragraph_selector">
              <label>Numero de paragrafos:</label>
              <div className="paragraph_buttons">
                {[3, 4, 5, 6, 7, 8, 10].map((num) => (
                  <button
                    key={num}
                    className={`paragraph_btn ${paragraphCount === num ? "active" : ""}`}
                    onClick={() => setParagraphCount(num)}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            <div className="article_preview">
              <h4>Estrutura do Artigo:</h4>
              <ul>
                <li><strong>Titulo:</strong> O titulo selecionado</li>
                <li><strong>Lead:</strong> Paragrafo inicial com os 5W+1H</li>
                <li><strong>Desenvolvimento:</strong> {paragraphCount} paragrafos em piramide invertida</li>
              </ul>
            </div>

            <div className="modal_actions">
              <button
                className="cancel_btn"
                onClick={() => setShowParagraphModal(false)}
              >
                Cancelar
              </button>
              <button className="confirm_btn" onClick={handleConfirmArticle}>
                Gerar Noticia
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
